import React, { useEffect, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Loader2, Lock, Fingerprint, Monitor, LogOut, ShieldCheck } from "lucide-react";
import { GlassField, GlassButton } from "@/components/GlassBits";
import { useUserAuth } from "@/context/UserAuthContext";
import { biometricSupported, registerBiometric } from "@/lib/biometric";

export const UserSecurity = () => {
  const { profile, signOut } = useUserAuth();
  const [data, setData] = useState(null);
  const [pin, setPin] = useState("");
  const [currentPin, setCurrentPin] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await userApi.get("/user/security");
      setData(data);
    } catch (e) { toast.error(apiError(e)); setData({ app_lock: {}, sessions: [] }); }
  };

  useEffect(() => { load(); }, []);

  const savePin = async () => {
    if (!/^\d{4,8}$/.test(pin)) return toast.error("El PIN debe tener de 4 a 8 dígitos");
    setBusy(true);
    try {
      await userApi.post("/user/security/pin", { pin, current_pin: currentPin || null });
      toast.success("PIN activado. Te lo pediremos al abrir la app.");
      setPin(""); setCurrentPin("");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const removePin = async () => {
    if (!/^\d{4,8}$/.test(currentPin)) return toast.error("Escribe tu PIN actual para quitarlo");
    setBusy(true);
    try {
      await userApi.delete(`/user/security/pin?pin=${currentPin}`);
      toast.success("Bloqueo desactivado");
      setCurrentPin("");
      sessionStorage.removeItem("app_unlocked");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleBiometric = async () => {
    const on = !data.app_lock?.biometric;
    setBusy(true);
    try {
      let credential_id = null;
      if (on) {
        if (!biometricSupported()) throw new Error("Este dispositivo no tiene huella o Face ID disponible");
        credential_id = await registerBiometric(profile?.account_key || "user", profile?.email);
      }
      await userApi.patch("/user/security/biometric", { enabled: on, credential_id });
      toast.success(on ? "Huella activada" : "Huella desactivada");
      await load();
    } catch (e) {
      toast.error(e.response ? apiError(e) : e.message || "No se pudo activar la huella");
    } finally { setBusy(false); }
  };

  const closeSession = async (sid, current) => {
    try {
      await userApi.delete(`/user/security/sessions/${sid}`);
      if (current) return signOut();
      toast.success("Sesión cerrada");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const closeOthers = async () => {
    try {
      const { data: res } = await userApi.post("/user/security/sessions/revoke-others");
      toast.success(`${res.closed} sesión(es) cerradas`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-red-400" />;
  const enabled = !!data.app_lock?.enabled;

  return (
    <div className="space-y-5 max-w-xl" data-testid="user-security">
      <div className="glass-card p-6">
        <div className="flex items-center gap-2 text-white font-semibold mb-1">
          <Lock className="w-4 h-4 text-red-400" /> Bloqueo de la app
        </div>
        <p className="text-xs text-white/55 mb-4">
          {enabled ? "Activado: te pedimos el PIN cada vez que abres la app."
            : "Crea un PIN de 4 a 8 dígitos para que nadie más pueda abrir tu panel."}
        </p>
        <div className="space-y-3">
          {enabled && (
            <GlassField label="PIN actual" type="password" inputMode="numeric" value={currentPin}
              data-testid="security-current-pin" placeholder="••••"
              onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 8))} />
          )}
          <GlassField label={enabled ? "PIN nuevo" : "PIN"} type="password" inputMode="numeric"
            value={pin} data-testid="security-new-pin" placeholder="4 a 8 dígitos"
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} />
        </div>
        <div className="flex gap-2 mt-4">
          <GlassButton tone="red" loading={busy} data-testid="security-save-pin" onClick={savePin}>
            {enabled ? "Cambiar PIN" : "Activar bloqueo"}
          </GlassButton>
          {enabled && (
            <GlassButton tone="ghost" data-testid="security-remove-pin" onClick={removePin}>
              Quitar bloqueo
            </GlassButton>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 mt-5 pt-4 border-t border-white/8">
          <div>
            <div className="text-sm text-white flex items-center gap-2">
              <Fingerprint className="w-4 h-4 text-red-400" /> Huella o Face ID
            </div>
            <div className="text-[11px] text-white/45">
              Desbloquea con la biometría de este dispositivo (el PIN sigue como respaldo)
            </div>
          </div>
          <GlassButton tone={data.app_lock?.biometric ? "red" : "neutral"} className="w-auto px-3 py-1.5 text-xs"
            data-testid="security-biometric" loading={busy} onClick={toggleBiometric}>
            {data.app_lock?.biometric ? "Desactivar" : "Activar"}
          </GlassButton>
        </div>
      </div>

      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-white font-semibold">
            <ShieldCheck className="w-4 h-4 text-red-400" /> Sesiones activas
          </div>
          <GlassButton tone="ghost" className="w-auto px-3 py-1.5 text-xs" data-testid="security-close-others"
            onClick={closeOthers}>Cerrar las demás</GlassButton>
        </div>
        <div className="space-y-2">
          {data.sessions.map((s) => (
            <div key={s.sid} className="flex items-center justify-between gap-3 border-b border-white/8 pb-2"
              data-testid={`session-${s.sid}`}>
              <div className="min-w-0">
                <div className="text-sm text-white flex items-center gap-2">
                  <Monitor className="w-3.5 h-3.5 text-white/50" /> {s.device}
                  {s.current && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-600 text-white">este</span>}
                </div>
                <div className="text-[11px] text-white/45 font-mono">
                  {s.ip || "—"} · {s.how} · {(s.last_seen || "").slice(0, 16).replace("T", " ")}
                </div>
              </div>
              <button data-testid={`close-session-${s.sid}`} onClick={() => closeSession(s.sid, s.current)}
                className="glass-btn px-2.5 py-1.5 rounded-lg text-[11px] flex items-center gap-1 shrink-0">
                <LogOut className="w-3 h-3" /> Cerrar
              </button>
            </div>
          ))}
          {data.sessions.length === 0 && <div className="text-xs text-white/50">Sin sesiones activas.</div>}
        </div>
      </div>
    </div>
  );
};

export default UserSecurity;
